/**
 * Socle commun des formulaires de demande de devis.
 *
 * Il y a quatre points de dépôt sur le site — le formulaire de l'accueil,
 * repris sur les pages communes et les satellites, /contact, /contact-devis, et
 * le rappel de l'estimateur de volume. Chacun avait sa copie de la même
 * logique, et les copies ont divergé. Ce qu'elles ont produit en une seule
 * journée :
 *
 *   · trois des quatre n'appelaient pas la notification par courriel ;
 *   · trois des quatre confirmaient au visiteur une demande que la base venait
 *     de refuser ;
 *   · les listes d'options ont divergé, si bien qu'une contrainte écrite
 *     d'après l'une rejetait ce que l'autre envoyait.
 *
 * Ce fichier porte les valeurs, la validation et l'envoi. Le rendu reste propre
 * à chaque page — les trois formulaires n'ont ni les mêmes libellés, ni la même
 * mise en page, et les unifier visuellement est une décision de contenu, pas de
 * code. Ce qui est mis en commun ici, c'est ce dont la divergence casse quelque
 * chose.
 */
import { useState } from 'react';
import { chargerSupabase } from './supabase-differe';
import { notifierDevis } from './notifierDevis';
import { mesurerDevisEnvoye, type SourceDevis } from './mesure';
import { ENTREPRISE } from '../data/entreprise';

// Date de « Dernière mise à jour » de la page /politique-confidentialite,
// transmise à l'ERP Spiroux avec le consentement (`consent.text_version`).
const VERSION_POLITIQUE_CONFIDENTIALITE = '2026-04';

// Le socle pur — valeurs, champs, validation, annotations pour les agents —
// vit dans `devis-schema.ts`, qui n'importe rien d'à effet de bord. Réexporté
// ici pour que les imports existants continuent de fonctionner tels quels.
export {
  SERVICES, VOLUMES, libelleDe, CHAMPS_VIDES, validerDevis,
  DESCRIPTIONS_AGENT, DESCRIPTION_OUTIL_DEVIS, NOM_OUTIL_DEVIS,
  descriptionDuChoix, BUDGET_DESCRIPTION_PARAM,
} from './devis-schema';
export type { OptionDevis, ChampsDevis, ErreursDevis } from './devis-schema';

import {
  SERVICES, VOLUMES, libelleDe, CHAMPS_VIDES, validerDevis,
  type ChampsDevis, type ErreursDevis,
} from './devis-schema';

// ---------------------------------------------------------------------------
// L'envoi
// ---------------------------------------------------------------------------
export interface OptionsFormulaireDevis {
  /** Origine, pour la mesure de conversion. */
  source: SourceDevis;
  libelleVilles?: 'ville' | 'adresse';
  /** Valeurs initiales, pour les pages qui pré-remplissent une commune. */
  valeursInitiales?: Partial<ChampsDevis>;
  /** Ajouté au message envoyé, sans être affiché — rattachement d'estimation. */
  suffixeMessage?: string;
  /**
   * Page d'origine du lead, écrite dans `devis_requests.source_page` et
   * reprise dans le courriel interne : chemin de la page, suivi de l'ancre
   * du bloc quand la page porte deux formulaires (`/#devis-hero`).
   *
   * Distinct de `source`, qui n'alimente que la mesure GA4 — donc rien de ce
   * que l'entreprise consulte, et seulement avec le consentement du visiteur.
   */
  pageOrigine?: string;
}

export function useFormulaireDevis(options: OptionsFormulaireDevis) {
  const {
    source, libelleVilles = 'ville',
    valeursInitiales, suffixeMessage, pageOrigine,
  } = options;

  const initial: ChampsDevis = { ...CHAMPS_VIDES, ...valeursInitiales };

  const [form, setForm] = useState<ChampsDevis>(initial);
  const [errors, setErrors] = useState<ErreursDevis>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [erreurEnvoi, setErreurEnvoi] = useState<string | null>(null);

  const set = <K extends keyof ChampsDevis>(champ: K, valeur: ChampsDevis[K]) =>
    setForm((prev) => ({ ...prev, [champ]: valeur }));

  const remplacer = (valeurs: Partial<ChampsDevis>) =>
    setForm((prev) => ({ ...prev, ...valeurs }));

  const envoyer = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setSubmitted(true);

    /**
     * `agentInvoked` — le seul chiffre du dispositif qui ne repose sur aucune
     * heuristique.
     *
     * C'est un booléen en lecture seule que le navigateur pose lui-même sur
     * l'événement `submit` d'un formulaire annoté : vrai si la soumission a été
     * déclenchée par un agent, faux si c'est une personne qui a cliqué. Aucune
     * détection d'user-agent, aucune inférence sur la vitesse de frappe — la
     * plateforme le sait, et le dit.
     *
     * Absent partout ailleurs que dans un Chrome où WebMCP est actif, d'où la
     * lecture prudente : `!== true` vaut faux, jamais `undefined`.
     */
    const parAgent =
      (ev.nativeEvent as Event & { agentInvoked?: boolean }).agentInvoked === true;

    const e = validerDevis(form, { libelleVilles });
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setErreurEnvoi(null);
    setLoading(true);

    const message = suffixeMessage ? `${form.message}\n${suffixeMessage}` : form.message;

    // Le SDK Supabase n'est téléchargé QU'ICI, sur la soumission — voir
    // lib/supabase-differe.ts. Le formulaire est rendu dans le hero de
    // l'accueil : l'importer statiquement mettait 48 Kio de SDK sur le
    // chemin critique de chaque page publique, pour un appel qui ne part
    // que sur un clic.
    // Même raison pour la télémétrie agentique : `socle/telemetry` ne sert
    // qu'à journaliser le RÉSULTAT de cette soumission, jamais au rendu.
    // Statique, il maintenait 11 Kio de plus dans le chunk d'entrée de
    // chaque page publique. Chargé en parallèle du SDK : les deux sont
    // nécessaires à la suite, aucun n'a besoin de l'autre.
    const [supabase, { logAgentEvent }] = await Promise.all([
      chargerSupabase(),
      import('../agentic/socle/telemetry'),
    ]);
    const { error } = await supabase.from('devis_requests').insert({
      service_type: form.service,
      firstname: form.firstName,
      lastname: form.lastName,
      email: form.email,
      phone: form.phone,
      departure_city: form.cityFrom,
      arrival_city: form.cityTo,
      move_date: form.date || null,
      volume: form.volume || 'unknown',
      message,
      // Colonne ajoutée par la migration 20260902120000, avec le GRANT
      // colonne par colonne que la clé anonyme exige. `null` et non
      // `undefined` : la ligne dit explicitement « non relevé ».
      source_page: pageOrigine ?? null,
    });

    setLoading(false);

    // La confirmation dépend du résultat. Trois formulaires sur quatre
    // l'affichaient sans le consulter : une contrainte violée ou une base
    // indisponible produisaient un « merci » indistinguable d'un succès, et la
    // demande n'existait nulle part.
    if (error) {
      logAgentEvent({
        type: 'form_submit',
        outil: 'demander_devis_demenagement',
        issue: 'error',
        // Le code de la base, pas son message : un code se regroupe, un
        // message libre ne se regroupe pas. C'est ce qui permettra de voir
        // qu'une contrainte CHECK rejette une valeur que le formulaire propose.
        codeErreur: error.code ?? 'insertion_refusee',
        agentInvoked: parAgent,
      });
      setErreurEnvoi(
        `Votre demande n'a pas pu être enregistrée. Merci de réessayer, ou de nous appeler au ${ENTREPRISE.telephone.affichage}.`,
      );
      return;
    }

    // Succès. Journalisé SANS `leadId` : le récupérer supposerait un
    // `.select()` après l'insertion, donc un droit de lecture anonyme sur
    // `devis_requests` — la table la plus sensible du projet, dont la migration
    // 20260809120000 a explicitement fermé la lecture. Un identifiant de
    // jointure ne vaut pas ça.
    //
    // Le rattachement existera pour les demandes créées par `request_quote`
    // (lot 3), qui passe par une Netlify Function en service_role : côté
    // serveur, l'identifiant revient sans qu'on ouvre quoi que ce soit. C'est
    // précisément le cas qui compte pour l'analyse agentique.
    logAgentEvent({
      type: 'form_submit',
      outil: 'demander_devis_demenagement',
      issue: 'ok',
      agentInvoked: parAgent,
      args: { service: form.service, volume: form.volume || 'unknown' },
    });

    // La base a accepté : c'est ici, et pas plus haut, que la conversion existe.
    mesurerDevisEnvoye(source, form.service);

    notifierDevis({
      service: libelleDe(SERVICES, form.service),
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      phone: form.phone,
      cityFrom: form.cityFrom,
      cityTo: form.cityTo,
      date: form.date,
      volume: form.volume ? libelleDe(VOLUMES, form.volume) : '',
      message,
      sourcePage: pageOrigine,
      form: source,
      // La case est obligatoire (`validerDevis` refuse l'envoi sans elle) :
      // arrivé ici, le consentement est donné. La version renvoie à la date de
      // la politique de confidentialité publiée — à suivre si elle change.
      consent: {
        given: form.privacy === true,
        text_version: VERSION_POLITIQUE_CONFIDENTIALITE,
        at: new Date().toISOString(),
      },
    });

    setSuccess(true);
    setSubmitted(false);
    setErrors({});
    setForm(initial);
    setTimeout(() => setSuccess(false), 6000);
  };

  return { form, set, remplacer, errors, submitted, loading, success, erreurEnvoi, envoyer };
}
